import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Loader2, Edit, Trash2, Folder, Tag, Calendar, CheckCircle, XCircle, ArrowLeft, Image as ImageIcon } from 'lucide-react'
import { categoryService, Category } from '@/services/categoryService'
import { DetailHeader } from '@/components/ui/DetailHeader'
import { DetailSection } from '@/components/ui/DetailSection'
import { Badge } from '@/components/ui/badge'
import { getImageUrl } from '@/config/imageConfig'

const CategoryDetails = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const [category, setCategory] = useState<Category | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [parentCategory, setParentCategory] = useState<Category | null>(null)
  const [subcategories, setSubcategories] = useState<Category[]>([])
  const [loadingSubcategories, setLoadingSubcategories] = useState(false)

  useEffect(() => {
    if (id) {
      loadCategory(id)
    }
  }, [id])

  const loadCategory = async (categoryId: string) => {
    try {
      setLoading(true)
      setError(null)
      const response = await categoryService.getById(categoryId)
      const cat = response.data
      setCategory(cat)

      // Load parent category if exists
      if (cat.parent_category_id) {
        try {
          const parentResponse = await categoryService.getById(cat.parent_category_id)
          setParentCategory(parentResponse.data)
        } catch {
          console.warn('Failed to load parent category')
        }
      }

      // Load subcategories
      await loadSubcategories(categoryId)
    } catch (err: any) {
      console.error('Error loading category:', err)
      setError(err.response?.data?.message || 'Échec du chargement des détails de la catégorie')
    } finally {
      setLoading(false)
    }
  }

  const loadSubcategories = async (categoryId: string) => {
    try {
      setLoadingSubcategories(true)
      const response = await categoryService.getSubcategories(categoryId)
      setSubcategories(response.data)
    } catch {
      console.warn('Failed to load subcategories')
    } finally {
      setLoadingSubcategories(false)
    }
  }

  const handleDelete = async () => {
    if (!category) return
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer « ${category.name} » ? Cette action est irréversible.`)) {
      try {
        await categoryService.delete(category.category_id)
        navigate('/categories')
      } catch (err: any) {
        console.error('Error deleting category:', err)
        alert(err.response?.data?.message || 'Échec de la suppression de la catégorie')
      }
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-6">
        <DetailHeader
          title="Détails de la catégorie"
          breadcrumbs={[
            { label: 'Catégories', href: '/categories' },
            { label: 'Détails', href: '#' }
          ]}
          backUrl="/categories"
        />
        <div className="bg-red-50 text-red-600 px-4 py-3 rounded-md">
          {error}
        </div>
      </div>
    )
  }

  if (!category) {
    return (
      <div className="space-y-6">
        <DetailHeader
          title="Détails de la catégorie"
          breadcrumbs={[
            { label: 'Catégories', href: '/categories' },
            { label: 'Détails', href: '#' }
          ]}
          backUrl="/categories"
        />
        <div className="bg-yellow-50 text-yellow-600 px-4 py-3 rounded-md">
          Catégorie introuvable
        </div>
      </div>
    )
  }

  const breadcrumbs = [
    { label: 'Catégories', href: '/categories' },
    { label: category.name, href: '#' }
  ]

  const actions = [
    {
      label: 'Modifier',
      icon: <Edit className="h-4 w-4" />,
      onClick: () => navigate(`/categories/${id}/edit`),
      variant: 'default' as const
    },
    {
      label: 'Supprimer',
      icon: <Trash2 className="h-4 w-4" />,
      onClick: handleDelete,
      variant: 'destructive' as const
    }
  ]

  const logoUrl = category.logo_url ? getImageUrl(`/uploads/categories/${category.logo_url}`) : null

  return (
    <div className="space-y-6">
      <DetailHeader
        title={category.name}
        subtitle={`Détails et gestion de la catégorie`}
        breadcrumbs={breadcrumbs}
        backUrl="/categories"
        actions={actions}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Information */}
        <div className="lg:col-span-2 space-y-6">
          <DetailSection title="Informations générales">
            <div className="space-y-4">
              <div className="flex items-center gap-4 mb-4">
                {/* Logo display */}
                {logoUrl ? (
                  <img
                    src={logoUrl}
                    alt={`${category.name} logo`}
                    className="h-16 w-16 object-cover rounded-lg border border-gray-200"
                  />
                ) : (
                  <div className="h-16 w-16 flex items-center justify-center rounded-lg border border-gray-200 bg-gray-50">
                    <ImageIcon className="h-8 w-8 text-gray-400" />
                  </div>
                )}
                <div>
                  <h3 className="text-2xl font-bold">{category.name}</h3>
                  <Badge variant={category.is_active ? 'default' : 'secondary'} className="inline-flex items-center mt-1">
                    {category.is_active ? (
                      <>
                        <CheckCircle className="mr-1 h-3 w-3" />
                        Actif
                      </>
                    ) : (
                      <>
                        <XCircle className="mr-1 h-3 w-3" />
                        Inactif
                      </>
                    )}
                  </Badge>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start gap-3">
                  <Tag className="h-5 w-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-500">ID de la catégorie</p>
                    <p className="text-sm text-gray-900 font-mono">{category.category_id}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Folder className="h-5 w-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-500">Catégorie parente</p>
                    <p className="text-sm text-gray-900">
                      {parentCategory ? (
                        <button
                          onClick={() => navigate(`/categories/${parentCategory.category_id}`)}
                          className="text-primary-600 hover:text-primary-800 underline"
                        >
                          {parentCategory.name}
                        </button>
                      ) : (
                        <span className="text-gray-500">Racine (aucun parent)</span>
                      )}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Calendar className="h-5 w-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-500">Créée le</p>
                    <p className="text-sm text-gray-900">{formatDate(category.created_at)}</p>
                  </div>
                </div>
              </div>
            </div>
          </DetailSection>

          {/* Description */}
          {category.description && (
            <DetailSection title="Description">
              <p className="text-gray-700 whitespace-pre-wrap">{category.description}</p>
            </DetailSection>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Subcategories */}
          <DetailSection title="Sous-catégories">
            {loadingSubcategories ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
              </div>
            ) : subcategories.length > 0 ? (
              <div className="space-y-2">
                {subcategories.map((sub) => (
                  <button
                    key={sub.category_id}
                    onClick={() => navigate(`/categories/${sub.category_id}`)}
                    className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-50 border border-gray-200 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-900">{sub.name}</span>
                      <Badge variant={sub.is_active ? 'default' : 'secondary'} className="text-xs">
                        {sub.is_active ? 'Actif' : 'Inactif'}
                      </Badge>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 py-2">Aucune sous-catégorie</p>
            )}
          </DetailSection>

          {/* Quick Actions */}
          <DetailSection title="Actions rapides">
            <div className="space-y-2">
              <button
                onClick={() => navigate(`/categories/${id}/edit`)}
                className="w-full inline-flex items-center justify-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                <Edit className="h-4 w-4 mr-2" />
                Modifier la catégorie
              </button>
              <button
                onClick={() => navigate('/categories')}
                className="w-full inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Retour à la liste
              </button>
            </div>
          </DetailSection>
        </div>
      </div>
    </div>
  )
}

export default CategoryDetails
